import { captureException } from '@sentry/browser';
import { AxiosError } from 'axios/index';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { EOrderStatus, TOrder } from '@/types/orders';

import { TErrorData, getQuestionnaire, getQuestionnairePaymentSchedule } from '@/shared/api';
import { PAYMENT_RENT_URL } from '@/shared/constants/paths';
import { useShowTechnicalError } from '@/shared/hooks/use-show-technical-error';

import { getQuestionnaireRequisites } from '../../api/requisites-service';
import { useRequisitesDialog } from '../use-requisites-dialog';

type TReturnHook = {
    isLoading: boolean;
    onOrderValidatePay: () => Promise<void>;
};

export const usePrepareOrderCard = (order: TOrder): TReturnHook => {
    const [isLoading, setIsLoading] = useState(false);

    const router = useRouter();

    const { showError } = useShowTechnicalError();

    const { showRequisitesFailure } = useRequisitesDialog();

    const onOrderValidatePay = async () => {
        if (order.status.type.includes(EOrderStatus.Sent_to_court)) return await defaultRentPay();

        switch (order.product?.term_unit) {
            case 1:
                return await extendRentPay();

            case 2:
                return await defaultRentPay();

            default:
                return;
        }
    };

    const fetchLoanProcessingTransactions = async (): Promise<{
        isError: boolean;
        isCards: boolean;
        isRepaymentAllow: boolean;
    }> => {
        let isError = false;

        let isCards = true;

        let isRepaymentAllow = false;

        try {
            const response = await Promise.all([
                getQuestionnaire(order.number),
                getQuestionnaireRequisites({ number: order.number, type: 'repayment' }),
            ]);

            if (response[0].payments_possibilities.repayment.allow) {
                isRepaymentAllow = true;
            }

            if (!response[1].data?.cards) {
                isCards = false;
            }

            isError = false;
        } catch (err) {
            const error = err as AxiosError;

            const response = error.response?.data as TErrorData;

            captureException(error);

            showError(response?.message);

            /* eslint-disable no-console */
            console.error('error fetch processing transactions: ', error);

            isError = true;
        } finally {
            // eslint-disable-next-line no-unsafe-finally
            return {
                isError,
                isCards,
                isRepaymentAllow,
            };
        }
    };

    const extendRentPay = async () => {
        setIsLoading(true);

        const response = await fetchLoanProcessingTransactions();

        if (!response.isError) {
            if (!response.isCards || !response.isRepaymentAllow) {
                showRequisitesFailure();
            } else {
                await router.push(`/account/orders/${order.number}/prolongation`);
            }
        }

        setIsLoading(false);
    };

    const defaultRentPay = async () => {
        setIsLoading(true);
        const response = await fetchLoanProcessingTransactions();

        if (!response.isError) {
            if (!response.isCards || !response.isRepaymentAllow) {
                showRequisitesFailure();
                setIsLoading(false);
            } else {
                try {
                    await getQuestionnairePaymentSchedule(order.number);

                    await router.push(`${PAYMENT_RENT_URL}/${order.number}`);
                } catch (e) {
                    showError('');
                } finally {
                    setIsLoading(false);
                }
            }
        }
    };

    return {
        isLoading,
        onOrderValidatePay,
    };
};
